import { BTitle, BForm } from '@prague-digi/ui-components'
import { compose } from 'recompose'
import { connect, ConnectedProps } from 'react-redux'
import classNames from '@prague-digi/class-names'
import get from 'lodash/get'
import React from 'react'

import {
  AMOUNT_INITIAL,
  DAY_OF_MOTH_FROM,
  DAY_OF_MOTH_INITIAL,
  DAY_OF_MOTH_TO,
  TERM_INITIAL,
} from '../../constants/productParameters'
import { API_ERROR } from '../../constants/modals'
import { ApiErrorInfo, Response } from '../../api/api'
import { appSelectors, modalDialogActions, productActions } from '../../redux'
import {
  BMoneyInput,
  Slider,
  BTextInput,
  BCombobox,
  PageControlButtons,
  PrefillFormButton,
} from '../'
import {
  compareValidValuesWithoutSpaces,
  findClosestInArray,
  getIntegerOrZero,
  notNullNorUndefined,
} from '../../utils/mix'
import { errorHandler, form, FormInterface, intl, IntlInterface } from '../../hoc'
import { LoanRange } from '../../redux/reducers/codetable/model'
import { ReduxState } from '../../types'
import formValidate from './LoanPreConfigurator.validate'
import messages from '../../i18n'
import styles from './styles.scss'

interface Props extends IntlInterface, FormInterface, ConnectedProps<typeof connector> {
  isMissingDataModal: boolean
  onClose?: any
  onContinue: any
  submitFailed: boolean
}

interface State {
  termSliderSteps: number[]
}

const FORM_NAME = 'LoanPreConfigurator'

export class LoanPreConfigurator extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    for (let i = DAY_OF_MOTH_FROM; i <= DAY_OF_MOTH_TO; i++) {
      this.instalmentDay.push({
        value: i,
        label: `${i}.`,
      })
    }

    let minLoanAmount: number = Number.MAX_VALUE
    let maxLoanAmount: number = Number.MIN_VALUE
    let terms: number[] = []
    props.loanRanges.map((item: LoanRange) => {
      minLoanAmount = Math.min(minLoanAmount, item.fromAmount)
      maxLoanAmount = Math.max(maxLoanAmount, item.toAmount)
      terms.push(...item.terms)
      return false
    })
    terms = Array.from(new Set(terms))

    const amountSteps: number[] = []
    const amountStep: number = 1000
    let actualAmount: number = minLoanAmount
    while (actualAmount <= maxLoanAmount) {
      amountSteps.push(actualAmount)
      actualAmount += amountStep
    }

    this.loanAmountSliderSteps = amountSteps

    this.state = {
      termSliderSteps: [],
    }

    props.initialize({
      ...props.storedData,
      loanAmountSlider: props.storedData.loanAmount,
      termSlider: props.storedData.term,
    })
  }

  componentDidMount() {
    if (this.props.isMissingDataModal) {
      let amount: number = AMOUNT_INITIAL
      let term: number = TERM_INITIAL
      this.props.loanRanges.map((value: LoanRange) => {
        if (value.fromAmount <= AMOUNT_INITIAL) {
          amount = value.fromAmount
          term = value.terms[value.terms.length - 1]
        }
        return false
      })

      this.props.change('loanAmount', amount)
      this.props.change('loanAmountSlider', amount)
      this.props.change('termSlider', term)
      this.props.change('term', term)
      this.props.change('instalmentDay', DAY_OF_MOTH_INITIAL)

      if (this.props.isLocalhost) {
        this.onSubmit()
      }
    } else {
      this.correctTerm(this.props.storedData.loanAmount)
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const actualLoanAmount = get(this.props, 'values.loanAmount')
    const previousLoanAmount = get(prevProps, 'values.loanAmount')

    if (
      !compareValidValuesWithoutSpaces(actualLoanAmount, previousLoanAmount) &&
      actualLoanAmount
    ) {
      this.correctTerm(getIntegerOrZero(actualLoanAmount))
    }
  }

  onSubmit = () => {
    this.props.handleSubmit(this.saveLoanPreConfiguratorCallback)()
  }

  onChangeLoanAmount = (value: number) => {
    this.props.change('loanAmount', value)
  }

  onChangeLoanAmountSlider = (value: any) => {
    this.props.change('loanAmountSlider', value)
  }

  onChangeTerm = (value: number) => {
    this.props.change('term', value)
  }

  onChangeTermSlider = (e: any) => {
    this.props.change('termSlider', e.target.value)
  }

  // Function also checks if the loan amount or term were changed during the user has clicked the submit button
  saveLoanPreConfiguratorCallback = () => {
    const now = new Date()

    if (this.loanRoundedTimestamp && now.getTime() - this.loanRoundedTimestamp.getTime() <= 200) {
      this.loanRoundedTimestamp = undefined
      this.props.showModalDialog({
        title: this.props.translate(messages.loanConfigurator.roundedLoanInfo, {
          loanRoundedFrom: this.loanRoundedFrom,
          loanRoundedTo: this.props.formatCzechCurrency(this.loanRoundedTo),
        }),
        closeButton: {
          text: messages.common.iUnderstand,
        },
      })
    } else if (
      this.termRoundedTimestamp &&
      now.getTime() - this.termRoundedTimestamp.getTime() <= 200
    ) {
      this.termRoundedTimestamp = undefined
      this.props.showModalDialog({
        title: this.props.translate(messages.loanConfigurator.roundedTermInfo, {
          termRoundedFrom: this.termRoundedFrom,
          termRoundedTo: this.termRoundedTo,
        }),
        closeButton: {
          text: messages.common.iUnderstand,
        },
      })
    } else {
      this.props.saveLoanPreConfigurator({
        success: () => {
          this.props.onContinue && this.props.onContinue()
          if (this.props.isMissingDataModal) {
            this.props.hideModalDialog('LOAN_DATA_MISSING')
          }
        },
        error: (errorInfo: ApiErrorInfo) => {
          if (errorInfo.type === Response.dataError && errorInfo.errors) {
            this.props.setErrors(FORM_NAME, errorInfo.errors)
          } else {
            this.props.showModalDialog(API_ERROR({ errorInfo, onRetry: this.onSubmit }))
          }
        },
      })
    }
  }

  roundTerm = (e: any) => {
    const value: any = findClosestInArray(e.target.value, this.state.termSliderSteps)

    if (!compareValidValuesWithoutSpaces(value, e.target.value)) {
      this.termRoundedFrom = e.target.value
      this.termRoundedTo = value
      this.termRoundedTimestamp = new Date()
    } else {
      this.termRoundedFrom = undefined
      this.termRoundedTo = undefined
      this.termRoundedTimestamp = undefined
    }

    if (notNullNorUndefined(value)) {
      setTimeout(() => {
        this.props.change('term', value)
        this.props.change('termSlider', value)
      })
    }
  }

  roundLoanAmount = (e: any) => {
    const value: any = findClosestInArray(e.target.value, this.loanAmountSliderSteps)

    if (!compareValidValuesWithoutSpaces(value, e.target.value)) {
      this.loanRoundedFrom = e.target.value
      this.loanRoundedTo = value
      this.loanRoundedTimestamp = new Date()
    } else {
      this.loanRoundedFrom = undefined
      this.loanRoundedTo = undefined
      this.loanRoundedTimestamp = undefined
    }

    if (notNullNorUndefined(value)) {
      setTimeout(() => {
        this.props.change('loanAmount', value)
        this.props.change('loanAmountSlider', value)
      })
    }
  }

  correctTerm = (loanAmount: number) => {
    const term: number = getIntegerOrZero(get(this.props.values, 'term'))

    this.props.loanRanges.some((item: LoanRange) => {
      if (loanAmount >= item.fromAmount && loanAmount <= item.toAmount) {
        this.setState(
          {
            termSliderSteps: [...item.terms],
          },
          () => {
            if (term < item.minTermMonths) {
              this.props.change('term', item.minTermMonths)
              this.props.change('termSlider', item.minTermMonths)
            } else if (term > item.maxTermMonths) {
              this.props.change('term', item.maxTermMonths)
              this.props.change('termSlider', item.maxTermMonths)
            }
          }
        )

        return true
      }
      return false
    })
  }

  loanAmountSliderSteps: number[] = []
  instalmentDay: any[] = []
  loanRoundedFrom: any | undefined
  loanRoundedTo: any | undefined
  loanRoundedTimestamp: Date | undefined
  termRoundedFrom: any | undefined
  termRoundedTo: any | undefined
  termRoundedTimestamp: Date | undefined

  render() {
    let loanAmountError = null
    let termError = null
    let instalmentDayError = null

    if (this.props.submitFailed) {
      loanAmountError =
        get(this.props, 'errors.loanAmount') || get(this.props, 'serverErrors.loanAmount')
      termError = get(this.props, 'errors.term') || get(this.props, 'serverErrors.term')
      instalmentDayError =
        get(this.props, 'errors.instalmentDay') || get(this.props, 'serverErrors.instalmentDay')
    }

    return (
      <div className={styles.loanConfigurator}>
        {this.props.isMissingDataModal ? (
          <BTitle center type="h3" theme="ml-4 mr-4">
            {this.props.translateHTML(messages.loanConfigurator.titleIsMissing)}
          </BTitle>
        ) : (
          <BTitle type="h3" center message={messages.loanConfigurator.titleDefault} />
        )}
        <BForm component="div">
          <div>
            <div className={styles.row}>
              <div>{this.props.translate(messages.loanConfigurator.loanAmount)}</div>
              <BMoneyInput
                name="loanAmount"
                noMessage
                onBlur={this.roundLoanAmount}
                onChange={this.onChangeLoanAmountSlider}
                theme={styles.moneyInput}
              />
            </div>
            {loanAmountError && <div className={styles.inputError}>{loanAmountError}</div>}
          </div>

          <div className={styles.row}>
            <Slider
              name="loanAmountSlider"
              testid="loanAmountSlider"
              rangeFrom={this.loanAmountSliderSteps[0]}
              rangeFromSuffix={this.props.translate(messages.common.CZK)}
              rangeTo={this.loanAmountSliderSteps[this.loanAmountSliderSteps.length - 1]}
              rangeToSuffix={this.props.translate(messages.common.CZK)}
              steps={this.loanAmountSliderSteps}
              hideSteps
              onChange={this.onChangeLoanAmount}
            />
          </div>

          <div>
            <div className={classNames(styles.row, styles.termRow)}>
              <div>{this.props.translate(messages.loanConfigurator.term)}</div>
              <div className={styles.termTextWrapper}>
                {this.props.formatLoanDuration(get(this.props, 'values.term', 0) || 0)}
              </div>
              <div className={styles.termInputWrapper}>
                <BTextInput
                  name="term"
                  testid="term"
                  onChange={this.onChangeTermSlider}
                  onBlur={this.roundTerm}
                  noMessage
                />
              </div>
            </div>
            {termError && <div className={styles.inputError}>{termError}</div>}
          </div>

          <div className={styles.row}>
            <Slider
              name="termSlider"
              testid="termSlider"
              rangeFrom={this.state.termSliderSteps[0]}
              rangeFromSuffix={this.props.translate(messages.loanConfigurator.termSliderRangeFrom)}
              rangeTo={this.state.termSliderSteps[this.state.termSliderSteps.length - 1]}
              rangeToSuffix={this.props.translate(messages.loanConfigurator.termSliderRangeTo)}
              steps={this.state.termSliderSteps}
              onChange={this.onChangeTerm}
            />
          </div>

          {this.props.isMissingDataModal ? (
            <PageControlButtons disableSubmit={this.props.invalid} onSubmit={this.onSubmit} />
          ) : (
            <>
              <div>
                <div className={styles.row}>
                  <div>{this.props.translate(messages.loanConfigurator.instalmentDay)}</div>
                  <BCombobox
                    name="instalmentDay"
                    testid="instalmentDay"
                    data={this.instalmentDay}
                    theme={styles.instalmentDay}
                    messages={{
                      placeholder: this.props.translate(messages.common.choose),
                    }}
                    noMessage
                    maxMenuHeight={120}
                  />
                </div>
                {instalmentDayError && (
                  <div className={styles.inputError}>{instalmentDayError}</div>
                )}
              </div>
              <PageControlButtons
                disableSubmit={this.props.invalid}
                onSubmit={this.onSubmit}
                submitMessage={messages.common.save}
                secondaryAction={this.props.onClose}
                secondaryMessage={messages.common.cancel}
              />
            </>
          )}
          <PrefillFormButton formName="LoanPreConfigurator" />
        </BForm>
      </div>
    )
  }
}

const connector = connect(
  (state: ReduxState) => ({
    storedData: {
      loanAmount: state.product.configuration?.loanAmount,
      term: state.product.configuration?.term,
      instalmentDay: state.product.instalmentDay,
    },
    loanRanges: state.codetable.loanRanges,
    isLocalhost: appSelectors.selectIsLocalhost(state),
  }),
  {
    saveLoanPreConfigurator: productActions.saveLoanPreConfigurator,
    showModalDialog: modalDialogActions.showModalDialog,
    hideModalDialog: modalDialogActions.hideModalDialog,
  }
)

export default compose(
  errorHandler('containers/Recapitulation/components/LoanPreConfigurator/LoanPreConfigurator.tsx'),
  connector,
  form({
    name: FORM_NAME,
    validate: formValidate,
  }),
  intl
)(LoanPreConfigurator)
